import React, { useEffect, useMemo, useState } from "react";
import ComponentCustomModal from "../../components/common/customModal/ComponentCustomModal";
import socket from "../../socket";
import CanvasJSReact from "@canvasjs/react-charts";
import { toast } from "react-toastify";

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const HotspotLiveBandwidth = ({ show, setShow, customer }) => {
  const [chunkData, setChunkData] = useState([]);

  const [dps1, setDps1] = useState([]);
  const [dps2, setDps2] = useState([]);
  const [xVal, setXVal] = useState(0);

  const dataLength = 150;

  // Helper function to format data to Mbps or Kbps
  const formatToMbps = (value) => {
    return (value / 1024).toFixed(2); // Convert to Kbps otherwise
  };

  useEffect(() => {
    if (!show && customer?.id) {
      socket.emit("stopCustomerLiveBandwidth", { customer: customer.id });

      setChunkData([]);
      setDps1([]);
      setDps2([]);
      setXVal(0);
    }
  }, [show, customer?.id]);

  useEffect(() => {
    if (customer?.id) {
      // Request server to start stream
      socket.emit("StaticCustomerLiveBandwidth", { customer: customer.id });

      // Handlers
      const handleBandwidthData = (data) => setChunkData(data);
      const handleBandwidthError = (err) => console.error("Error:", err);
      const handleBandwidthStopped = (data) => toast.success(data);

      // Register listeners
      socket.on("bandwidthData", handleBandwidthData);
      socket.on("bandwidthError", handleBandwidthError);
      socket.on("bandwidthStopped", handleBandwidthStopped);

      // Cleanup (remove all listeners to avoid duplicate firing)
      return () => {
        socket.off("bandwidthData", handleBandwidthData);
        socket.off("bandwidthError", handleBandwidthError);
        socket.off("bandwidthStopped", handleBandwidthStopped);
      };
    }
  }, [customer?.id]);

  useEffect(() => {
    if (chunkData) {
      const rxValue = Number(chunkData[0]?.rx) || 0;
      const txValue = Number(chunkData[0]?.tx) || 0;

      // Update data points for both download and upload
      setDps1((prevDps1) => {
        const newDps1 = [
          ...prevDps1,
          { x: xVal, y: Number(formatToMbps(rxValue)) },
        ];
        return newDps1.length > dataLength ? newDps1.slice(1) : newDps1;
      });

      setDps2((prevDps2) => {
        const newDps2 = [
          ...prevDps2,
          { x: xVal, y: Number(formatToMbps(txValue)) },
        ];
        return newDps2.length > dataLength ? newDps2.slice(1) : newDps2;
      });

      setXVal((prevXVal) => prevXVal + 1); // Increment xVal for the next update
    }
  }, [chunkData]);

  const options = useMemo(
    () => ({
      exportEnabled: true,
      // title: {
      //   text: "Real-time Bandwidth Usage",
      // },
      axisX: {
        minimum: xVal - dataLength + 1, // Dynamic x-axis start point
        maximum: xVal, // Current xVal as end point
      },
      data: [
        {
          type: "column",
          showInLegend: true,
          legendText: `Upload: ${
            dps1[dps1.length - 1]?.y / 1024 >= 1
              ? (dps1[dps1.length - 1]?.y / 1024).toFixed(2)
              : dps1[dps1.length - 1]?.y
          } ${Number(dps1[dps1.length - 1]?.y / 1024) >= 1 ? "Mbps" : "kbps"}`,
          dataPointWidth: 2,
          dataPoints: dps1,
        },
        {
          type: "column",
          showInLegend: true,
          legendText: `Download: ${
            dps2[dps2.length - 1]?.y / 1024 >= 1
              ? (dps2[dps2.length - 1]?.y / 1024).toFixed(2)
              : dps2[dps2.length - 1]?.y
          } ${Number(dps2[dps2.length - 1]?.y / 1024) >= 1 ? "Mbps" : "kbps"}`,
          dataPointWidth: 2,
          dataPoints: dps2,
        },
      ],
    }),
    [xVal, dps1, dps2]
  );

  return (
    <>
      <ComponentCustomModal
        show={show}
        setShow={setShow}
        centered
        size="lg"
        header={"bandwidth"}
      >
        <div>
          <CanvasJSChart options={options} />
        </div>
      </ComponentCustomModal>
    </>
  );
};

export default HotspotLiveBandwidth;
